/**
 * Procedural synthwave-ish background bed — entirely synthesised, no asset file
 * (the 2013 soundtrack was copyrighted commercial music and can't ship).
 *
 * A detuned saw pad + sub bass + a soft arpeggio over a 4-chord minor loop.
 */

const A2 = 110;
const SEMI = 2 ** (1 / 12);
const note = (semitonesFromA2: number): number => A2 * SEMI ** semitonesFromA2;

// i - VI - III - VII in A minor, as semitone offsets from A2.
const PROGRESSION = [0, -4, 3, -2];
const CHORD_SECONDS = 4;
const ARP_SECONDS = 0.25;

export class Music {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private padFilter: BiquadFilterNode | null = null;
  private voices: OscillatorNode[] = [];
  private bass: OscillatorNode | null = null;
  private timers: ReturnType<typeof setInterval>[] = [];
  private chordIndex = 0;
  playing = false;

  toggle(): boolean {
    if (this.playing) this.stop();
    else this.start();
    return this.playing;
  }

  start(): void {
    if (this.playing) return;
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;

    const ctx = new Ctor();
    void ctx.resume();
    this.ctx = ctx;

    this.master = ctx.createGain();
    this.master.gain.value = 0;
    this.master.gain.linearRampToValueAtTime(0.14, ctx.currentTime + 2);
    this.master.connect(ctx.destination);

    // Pad: three detuned saws through a resonant lowpass with a slow LFO.
    this.padFilter = ctx.createBiquadFilter();
    this.padFilter.type = "lowpass";
    this.padFilter.frequency.value = 500;
    this.padFilter.Q.value = 7;
    const padGain = ctx.createGain();
    padGain.gain.value = 0.5;
    this.padFilter.connect(padGain);
    padGain.connect(this.master);

    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.07;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 260;
    lfo.connect(lfoGain);
    lfoGain.connect(this.padFilter.frequency);
    lfo.start();
    this.voices.push(lfo);

    for (const detune of [-6, 0, 7]) {
      const osc = ctx.createOscillator();
      osc.type = "sawtooth";
      osc.detune.value = detune;
      osc.connect(this.padFilter);
      osc.start();
      this.voices.push(osc);
    }

    this.bass = ctx.createOscillator();
    this.bass.type = "sine";
    const bassGain = ctx.createGain();
    bassGain.gain.value = 0.4;
    this.bass.connect(bassGain);
    bassGain.connect(this.master);
    this.bass.start();

    this.playing = true;
    this.setChord(0);
    this.timers.push(
      setInterval(
        () => this.setChord((this.chordIndex + 1) % PROGRESSION.length),
        CHORD_SECONDS * 1000,
      ),
    );
    this.timers.push(setInterval(() => this.arpNote(), ARP_SECONDS * 1000));
  }

  stop(): void {
    for (const t of this.timers) clearInterval(t);
    this.timers = [];
    const ctx = this.ctx;
    if (ctx && this.master) {
      this.master.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.6);
      setTimeout(() => void ctx.close(), 800);
    }
    this.voices = [];
    this.bass = null;
    this.ctx = null;
    this.playing = false;
  }

  private setChord(index: number): void {
    this.chordIndex = index;
    const ctx = this.ctx;
    if (!ctx) return;
    const root = PROGRESSION[index]!;
    const freqs = [note(root), note(root + 7), note(root + 12)];
    const pad = this.voices.filter((v) => v.type === "sawtooth");
    pad.forEach((osc, i) => {
      osc.frequency.setTargetAtTime(freqs[i] ?? freqs[0]!, ctx.currentTime, 0.4);
    });
    this.bass?.frequency.setTargetAtTime(note(root - 12), ctx.currentTime, 0.2);
  }

  private arpNote(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const root = PROGRESSION[this.chordIndex]!;
    const steps = [0, 3, 7, 12, 7, 3];
    const step = steps[Math.floor((Date.now() / (ARP_SECONDS * 1000)) % steps.length)]!;
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = note(root + 24 + step);
    const g = ctx.createGain();
    const t = ctx.currentTime;
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.09, t + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
    osc.connect(g);
    g.connect(this.master);
    osc.start(t);
    osc.stop(t + 0.3);
  }
}
